import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { WorkersService } from './workers.service';
import { ActivatedRoute, RouterLink, RouterLinkActive } from '@angular/router';
import {
  ButtonComponent,
  HashratePipe,
  IconButtonComponent,
  IPaginationEvent,
  ModalService,
  PaginationComponent,
  SpecialIcon,
  SpecialIconComponent,
  SvgIconComponent,
  TableColumnItem,
  TableComponent,
} from '@ui-lib/components';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { getWorkersTableColumnsConfig } from './workers-table-columns.config';
import { LoadingState } from '../../shared/types/loading.type';
import {
  IWorkerModalData,
  WorkerAction,
  WorkersFilterForm,
  SavedWorkersFilterForm,
} from './models';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { classByStatus, isLoadingStatus } from './helpers';
import { LocalStorageService } from '../../core/services/storage/local-storage.service';
import { CoinSelectorComponent } from '../../shared/components/coin-selector/coin-selector.component';
import { PoolSelectorComponent } from '../../shared/components/pool-selector/pool-selector.component';
import { WorkerStatusSelectorComponent } from '../../shared/components/worker-status-selector/worker-status-selector.component';
import { IDictionaryItem } from '../../shared/types/dictionary-item.interface';
import { AsyncPipe } from '@angular/common';
import { prepareRequest } from '../../shared/utils/prepare-request.function';
import { SearchWorkersResponse, WorkerSources } from '../../core/openapi/bff-admin-api';
import { WorkerModalComponent } from './components/worker-modal/worker-modal.component';
import { PageNavItem } from '../../shared/types/page-nav-item.type';
import { ConfirmationDialogComponent } from '../../shared/components/confirm-dialog/confirmation-dialog.component';
import { IConfirmationDialogData } from '../../shared/components/confirm-dialog/confirmation-dialog-data.interface';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PageToolbarAction } from '../../shared/components/page-toolbar/page-toolbar.model';

const WORKER_FILTERS = 'workerFilters';

@Component({
  standalone: true,
  selector: 'mon-workers',
  templateUrl: './workers.component.html',
  styleUrls: ['./workers.component.scss'],
  imports: [
    ButtonComponent,
    SvgIconComponent,
    TranslateModule,
    TableComponent,
    HashratePipe,
    IconButtonComponent,
    ReactiveFormsModule,
    PaginationComponent,
    SpecialIconComponent,
    CoinSelectorComponent,
    PoolSelectorComponent,
    WorkerStatusSelectorComponent,
    AsyncPipe,
    RouterLink,
    RouterLinkActive,
    PageToolbarComponent,
  ],
  providers: [WorkersService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkersComponent implements OnInit, OnDestroy {
  private readonly workersService = inject(WorkersService);
  private readonly storageService = inject(LocalStorageService);
  private readonly modalService = inject(ModalService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);

  private readonly subscriptionDestroy$: Subject<void> = new Subject<void>();

  public readonly currentSource: WorkerSources =
    (this.activatedRoute.snapshot.data['workerSource'] as WorkerSources | undefined) ??
    WorkerSources.Billing;
  public readonly isManualWorkersPage = this.currentSource === WorkerSources.Manual;
  public readonly WorkerAction = WorkerAction;
  public readonly canManageWorkers = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.WorkerManage),
    { initialValue: false },
  );
  public readonly columns = computed<TableColumnItem[]>(() =>
    getWorkersTableColumnsConfig(this.isManualWorkersPage && this.canManageWorkers()),
  );
  public readonly pageNavigation: PageNavItem[] = [
    {
      id: 1,
      url: '/workers',
      titleTranslateKey: 'monitoring.workers.tabs.billing',
    },
    {
      id: 2,
      url: '/workers/manual',
      titleTranslateKey: 'monitoring.workers.tabs.manual',
    },
  ];

  public readonly currentWorkerPage: WritableSignal<number> = signal<number>(1);

  public readonly filterForm = new FormGroup<WorkersFilterForm>({
    keyword: new FormControl<string>('', { nonNullable: true }),
    coinIds: new FormControl<number[]>([], { nonNullable: true }),
    poolIds: new FormControl<string[]>([], { nonNullable: true }),
    statusIds: new FormControl<number[]>([], { nonNullable: true }),
  });

  public readonly LoadingState = LoadingState;
  public readonly classByStatus = classByStatus;
  public readonly isLoadingStatus = isLoadingStatus;

  public selectedCoins: IDictionaryItem<number>[] = [];
  public selectedPools: IDictionaryItem<string>[] = [];
  public selectedStatuses: IDictionaryItem<number>[] = [];

  public readonly workersLoadingStatus: Signal<LoadingState> =
    this.workersService.workersLoadingStatus;
  public readonly workerDeleteLoadingStatus: Signal<LoadingState> =
    this.workersService.workerDeleteLoadingStatus;
  public readonly workersDataCount: Signal<number> = this.workersService.workersDataCount;
  public readonly workers$ = this.workersService.workers$;

  public ngOnInit(): void {
    const savedFilters = this.storageService.getParsed<SavedWorkersFilterForm>(
      this.workerFiltersStorageKey,
    );

    if (savedFilters) {
      this.filterForm.patchValue(
        {
          keyword: savedFilters.keyword ?? '',
          coinIds: savedFilters.coinIds ?? [],
          poolIds: savedFilters.poolIds ?? [],
          statusIds: savedFilters.statusIds ?? [],
        },
        { emitEvent: false },
      );
    }

    this.applyFilters();

    this.filterForm.valueChanges
      .pipe(takeUntil(this.subscriptionDestroy$))
      .subscribe(() => this.applyFilters());
  }

  private applyFilters(): void {
    this.currentWorkerPage.set(1);

    const filters = this.filterForm.getRawValue();
    const preparedFilters = prepareRequest(filters);

    this.storageService.set(this.workerFiltersStorageKey, preparedFilters);
    this.workersService.changeReqParams({
      ...preparedFilters,
      skip: 0,
      source: this.currentSource,
    });
  }

  public get searchControl(): FormControl<string> {
    return this.filterForm.controls.keyword;
  }

  public get coinControl(): FormControl<number[]> {
    return this.filterForm.controls.coinIds;
  }

  public get poolControl(): FormControl<string[]> {
    return this.filterForm.controls.poolIds;
  }

  public get statusControl(): FormControl<number[]> {
    return this.filterForm.controls.statusIds;
  }

  public get isFiltersActive(): boolean {
    return (
      this.searchControl.value.length > 0 ||
      this.coinControl.value.length > 0 ||
      this.poolControl.value.length > 0 ||
      this.statusControl.value.length > 0
    );
  }

  public get selectedFiltersCount(): number {
    return this.selectedCoins.length + this.selectedPools.length + this.selectedStatuses.length;
  }

  protected toolbarActions(isEmptyState: boolean): PageToolbarAction[] {
    const actions: PageToolbarAction[] = [
      {
        id: 'export',
        icon: 'download',
        titleTranslateKey: 'monitoring.workers.dashboard.export',
        disabled: this.workersService.workerExportLoadingStatus() === LoadingState.LOADING,
      },
    ];

    return !isEmptyState && this.isManualWorkersPage && this.canManageWorkers()
      ? [
          {
            id: 'create',
            icon: 'plus',
            titleTranslateKey: 'monitoring.workers.dashboard.add-worker',
          },
          ...actions,
        ]
      : actions;
  }

  protected onToolbarAction(actionId: string): void {
    if (actionId === 'create') {
      this.openWorkerModal(WorkerAction.Create);
    }

    if (actionId === 'export') {
      this.workersService.exportWorkers();
    }
  }

  public clearAllFilters(): void {
    if (!this.isFiltersActive) return;

    this.filterForm.patchValue({
      keyword: '',
      coinIds: [],
      poolIds: [],
      statusIds: [],
    });
  }

  public removeFilter(
    type: 'coinIds' | 'poolIds' | 'statusIds',
    id: number | string | undefined,
  ): void {
    if (id === undefined) return;

    if (type === 'coinIds' || type === 'statusIds') {
      const control = type === 'coinIds' ? this.coinControl : this.statusControl;
      control.setValue(control.value.filter((value) => value !== Number(id)));
      return;
    }

    this.poolControl.setValue(this.poolControl.value.filter((value) => value !== String(id)));
  }

  public onCoinsSelectionChange(items: IDictionaryItem<number>[]): void {
    this.selectedCoins = items;
  }

  public onPoolsSelectionChange(items: IDictionaryItem<string>[]): void {
    this.selectedPools = items;
  }

  public onStatusesSelectionChange(items: IDictionaryItem<number>[]): void {
    this.selectedStatuses = items;
  }

  public onPageChange(paginator: IPaginationEvent): void {
    this.currentWorkerPage.set(paginator.pageIndex);
    this.workersService.changeReqParams({
      skip: (paginator.pageIndex - 1) * paginator.pageSize,
      take: paginator.pageSize,
    });
  }

  public typedRow(row: unknown): SearchWorkersResponse {
    return row as SearchWorkersResponse;
  }

  public specialIconType(row: string): SpecialIcon {
    return row as SpecialIcon;
  }

  public openWorkerModal(action: WorkerAction, worker?: SearchWorkersResponse): void {
    if (!this.canManageWorkers()) {
      return;
    }

    if (action === WorkerAction.Create && !this.isManualWorkersPage) {
      return;
    }

    this.modalService
      .open<IWorkerModalData, boolean>({
        component: WorkerModalComponent,
        customContainerClass: 'worker-modal',
        data: {
          action,
          workerId: worker?.id,
          worker,
        },
      })
      .afterClose$.pipe(
        filter((result) => !!result),
        take(1),
        takeUntil(this.subscriptionDestroy$),
      )
      .subscribe(() => {
        if (action === WorkerAction.Create) {
          this.currentWorkerPage.set(1);
          this.workersService.changeReqParams({ skip: 0 });
          return;
        }

        this.workersService.reloadWorkers();
      });
  }

  public confirmDeleteWorker(worker: SearchWorkersResponse): void {
    if (!this.canManageWorkers() || !this.isManualWorkersPage || !worker.isEditable) {
      return;
    }

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        customContainerClass: 'confirmation-dialog',
        data: {
          chip: worker.name,
          entityName:
            worker.displayName && worker.displayName !== worker.name
              ? worker.displayName
              : undefined,
          headerTranslationKey: 'monitoring.workers.dialog.delete.title',
          descriptionText:
            '\u0412\u044b \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043b\u044c\u043d\u043e ' +
            '\u0445\u043e\u0442\u0438\u0442\u0435 \u0443\u0434\u0430\u043b\u0438\u0442\u044c ' +
            '\u0432\u043e\u0440\u043a\u0435\u0440\u0430? \u041e\u043d \u0431\u0443\u0434\u0435\u0442 ' +
            '\u0443\u0434\u0430\u043b\u0435\u043d \u043d\u0430\u0432\u0441\u0435\u0433\u0434\u0430. ' +
            '\u0412\u043e\u0441\u0441\u0442\u0430\u043d\u043e\u0432\u0438\u0442\u044c \u0431\u0443\u0434\u0435\u0442 ' +
            '\u043d\u0435\u0432\u043e\u0437\u043c\u043e\u0436\u043d\u043e.',
          approvalTranslationKey: 'monitoring.workers.dialog.delete.confirm',
          cancellationTranslationKey: 'monitoring.workers.dialog.delete.cancel',
        },
      })
      .afterClose$.pipe(
        filter((result) => !!result),
        take(1),
        takeUntil(this.subscriptionDestroy$),
      )
      .subscribe(() => {
        this.handleDeleteWorker(worker.id);
      });
  }

  private handleDeleteWorker(workerId: string): void {
    if (!this.canManageWorkers()) {
      return;
    }

    this.workersService
      .deleteWorker(workerId)
      .pipe(
        filter((result) => !!result),
        take(1),
        takeUntil(this.subscriptionDestroy$),
      )
      .subscribe(() => {
        this.workersService.reloadWorkers();
      });
  }

  private get workerFiltersStorageKey(): string {
    return `${WORKER_FILTERS}:${this.currentSource}`;
  }

  public ngOnDestroy(): void {
    this.subscriptionDestroy$.next();
    this.subscriptionDestroy$.complete();
  }
}
